/**
 * Déconnexion complète — Business-Session beenden → Login-Seite (Benutzer/Passwort).
 *
 * Phase L1: logoutClearEpoch invalidates in-flight persistent clears so a fast
 * re-login cannot be wiped by the previous logout's background IndexedDB clear.
 */

import { kvGet, kvRemove, kvRemovePersisted, kvKeysStartingWith } from '../infrastructure/storage/kvAccess';
import { sessionRemove } from '../infrastructure/storage/sessionAccess';
import { clearRoleSessionStorage, clearBusinessPauseFlags } from '../utils/roleSessionLogout';
import { clearRolePinSession } from '../utils/rolePinSession';
import { markBusinessLogoutIntent } from '../utils/businessLogoutFlag';
import {
  resetBusinessBootstrapSession
} from '../features/persistence/businessBootstrapSession';

const CODE_LOCKOUT_KEY = 'codeEntryLockout';
/** Cashier login handoff (sessionStorage) — must not survive into the next session. */
const PENDING_WORKER_CASH_LOGIN_KEY = 'pendingWorkerCashLogin';

const BUSINESS_SESSION_KEYS = [
  'currentUser',
  'currentBusiness',
  'managerCode',
  CODE_LOCKOUT_KEY,
  'currentRole',
  'workerDashboardState'
];

/** Bumped on logout start and on every new login session establish. */
let logoutClearEpoch = 0;

/** Invalidate any in-flight logout persistent-clear (call when establishing a new session). */
export function bumpLogoutClearEpoch(reason = 'manual') {
  void reason;
  logoutClearEpoch += 1;
  return logoutClearEpoch;
}

export function getLogoutClearEpoch() {
  return logoutClearEpoch;
}

/** @param {number} epoch */
export function isLogoutClearEpochCurrent(epoch) {
  return epoch === logoutClearEpoch;
}

export function redirectToLogin(options = {}) {
  const { forceReload = false } = options;
  const isElectron = typeof window !== 'undefined' && window.electronAPI;

  // Electron + file://: Full-Reload bricht oft → weißer Bildschirm + Zombie-Prozess
  // Soft-Redirect reicht, wenn React bereits currentUser=null hat (Login gemountet).
  if (isElectron && !forceReload) {
    try {
      if (window.location.hash !== '#/' && window.location.hash !== '') {
        window.location.hash = '#/';
      }
    } catch (_) {
      /* ignore */
    }
    return;
  }

  const base = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  window.location.replace(`${base}#/`);
  if (!isElectron || forceReload) {
    window.location.reload();
  }
}

/**
 * Synchron: KV-Session leeren (sofort, vor Redirect).
 * Always clears bootstrap warm memory — next login reloads from storage.
 */
export function clearBusinessSessionSync(_options = {}) {
  void _options;
  markBusinessLogoutIntent();
  kvRemove('currentUser');
  kvRemove('currentBusiness');
  kvRemove('managerCode');
  kvRemove(CODE_LOCKOUT_KEY);
  // An aborted cashier handoff would otherwise reopen the cash modal without a PIN.
  sessionRemove(PENDING_WORKER_CASH_LOGIN_KEY);
  clearRolePinSession();
  clearRoleSessionStorage(null);
  clearBusinessPauseFlags(null);
  resetBusinessBootstrapSession({ allowAuthLoad: true });
}

/** Wartet auf IndexedDB + entfernt Legacy-localStorage-Kopien. */
export async function persistentlyClearBusinessSession(options = {}) {
  const shouldAbort =
    typeof options.shouldAbort === 'function' ? options.shouldAbort : () => false;

  if (shouldAbort()) return { aborted: true };

  const roleKeys = kvKeysStartingWith('currentRole_');
  const pauseKeys = kvKeysStartingWith('cash_register_paused_');
  const keys = [...new Set([...BUSINESS_SESSION_KEYS, ...roleKeys, ...pauseKeys])];

  for (const key of keys) {
    if (shouldAbort()) return { aborted: true };
    await kvRemovePersisted(key);
  }
  return { aborted: false };
}

function isBusinessSessionStillPresent() {
  if (
    BUSINESS_SESSION_KEYS.some((key) => {
      const value = kvGet(key);
      return value != null && value !== '';
    })
  ) {
    return true;
  }
  return kvKeysStartingWith('currentRole_').some((key) => {
    const value = kvGet(key);
    return value != null && value !== '';
  });
}

/** Löscht Session synchron + persistent; wiederholt einmal bei Restdaten. */
export async function verifyAndClearBusinessSession() {
  clearBusinessSessionSync();
  await persistentlyClearBusinessSession();
  if (isBusinessSessionStillPresent()) {
    await persistentlyClearBusinessSession();
  }
  return !isBusinessSessionStillPresent();
}

/**
 * Vor Backup-/Reinstall-Recovery: ausstehenden Logout anwenden.
 * @returns {Promise<{ applied: boolean, cleared?: boolean }>}
 */
export async function applyPendingLogoutBeforeSessionRecovery() {
  const { isBusinessLogoutInProgress } = await import('../utils/businessLogoutFlag');
  if (!isBusinessLogoutInProgress()) {
    return { applied: false };
  }

  const cleared = await verifyAndClearBusinessSession();
  if (cleared) {
    const { clearBusinessLogoutIntent } = await import('../utils/businessLogoutFlag');
    clearBusinessLogoutIntent();
  }
  return { applied: true, cleared };
}

/**
 * One sign-out call only: authService.signOut() already wraps supabase.auth.signOut(),
 * so calling both fired two network requests per logout (RL10).
 */
function signOutCloudInBackground() {
  void (async () => {
    try {
      const { authService } = await import('./supabaseService');
      if (typeof authService?.signOut === 'function') {
        await authService.signOut();
        return;
      }
    } catch (error) {
      console.warn('[completeLogout] authService.signOut:', error?.message || error);
    }

    try {
      const { supabase } = await import('../config/supabase');
      await supabase.auth.signOut();
    } catch (error) {
      console.warn('[completeLogout] Supabase signOut:', error?.message || error);
    }
  })();
}

function notifyLoggedOut() {
  try {
    window.dispatchEvent(new CustomEvent('authStateChanged', { detail: { user: null } }));
  } catch (_) {
    /* ignore */
  }
}

/**
 * Sofort UI auf Login (sync clear + Event), Persistenz im Hintergrund.
 * Logout-Intent bleibt bis Login gemountet / erfolgreichem Login —
 * verhindert Manager-Code-Modal statt Accueil.
 *
 * Always clears in-memory business warm caches — next login loads from storage.
 *
 * @param {{ reload?: boolean }} [options]
 *   reload=true → Browser Full-Reload (Fallback).
 *   reload=false → nur React (Electron / smooth path). Default false.
 */
export async function completeLogout(options = {}) {
  const { reload = false } = options;

  // New epoch — any older background clear must stop (fast re-login race).
  const clearEpoch = bumpLogoutClearEpoch('logout-start');

  // Capture before sync clear — business-scoped warm readers need the prior id
  let previousBusinessId = null;
  try {
    const { resolveAuthSessionKeyFromStorage } = await import(
      '../features/persistence/resolveAuthSession'
    );
    previousBusinessId = resolveAuthSessionKeyFromStorage() || null;
  } catch (_) {
    try {
      const { getCurrentBusinessId } = await import('../utils/sharedCore');
      previousBusinessId = getCurrentBusinessId({ allowFallback: false }) || null;
    } catch {
      /* optional */
    }
  }

  markBusinessLogoutIntent();
  clearBusinessSessionSync();

  try {
    const { teardownAllRouteKeepAlives } = await import(
      '../features/navigation/routeKeepAlive'
    );
    teardownAllRouteKeepAlives('hard-logout');
  } catch (_) {
    /* optional */
  }

  try {
    const { clearBusinessWarmCaches } = await import(
      '../features/persistence/clearBusinessWarmCaches'
    );
    await clearBusinessWarmCaches(previousBusinessId);
  } catch (_) {
    /* optional */
  }
  try {
    const { clearDerivedDashboardModelCaches } = await import(
      '../domain/dashboard/derivedModelCache'
    );
    clearDerivedDashboardModelCaches();
  } catch (_) {
    /* optional */
  }

  // UI sofort → Login (nicht auf IndexedDB warten)
  notifyLoggedOut();
  signOutCloudInBackground();

  // Persistenz im Hintergrund — Intent bleibt gesetzt; abort if re-login bumped epoch
  void (async () => {
    const shouldAbort = () => !isLogoutClearEpochCurrent(clearEpoch);
    try {
      const first = await persistentlyClearBusinessSession({ shouldAbort });
      if (first.aborted || shouldAbort()) return;
      if (isBusinessSessionStillPresent()) {
        if (shouldAbort()) return;
        await persistentlyClearBusinessSession({ shouldAbort });
      }
    } catch (err) {
      console.warn('[completeLogout] persistent clear:', err?.message || err);
    }
  })();

  if (reload) {
    redirectToLogin({ forceReload: true });
    return;
  }

  redirectToLogin({ forceReload: false });
}
